import {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import utilStyles from '~/styles/utils.module.css';
import summaryStyles from '~/styles/bookingFlow/summary.module.css';
import InformationCard from '~/components/BookingFlow/Information/InformationCard';
import useTranslation from '~/hooks/useTranslation';
import PriceStatistic from '~/components/BookingFlow/PriceStatistic';
import SummaryMainBooker from '~/components/BookingFlow/Summary/MainBooker';
import SummaryAddressInformation from '~/components/BookingFlow/Summary/AddressInformation';
import formatDate from '~/helpers/formatDate';
import useMutatePackageConfirmation from '~/hooks/bookingFlow/usePackageConfirmation';
import Button from '~/components/General/Button';
import { useOptions } from '~/hooks/useOptions';
import { BookingFlowSummaryContext } from '~/context/BookingFlowSummaryProvider';
import { InferredBookingFlowOptions } from '~/types/options';
import bookingFlowRoutes from '~/routes/bookingFlow';
import Summary from '~/components/BookingFlow/Summary';
import SummaryPromoCode from '~/components/BookingFlow/Summary/PromoCode';
import SummaryRoomTypes from '~/components/BookingFlow/Summary/SummaryRoomTypes';
import IconArrowRight from '~icons/tabler/arrow-right';
import IconArrowLeft from '~icons/tabler/arrow-left';
import BookingFlowLayout from '../Layout/Layout';

function BookingFlowSummary() {
  const options = useOptions() as InferredBookingFlowOptions;
  const [isLoading, setIsLoading] = useState(false);
  const translate = useTranslation();
  const navigate = useNavigate();
  const packageConfirmationMutation = useMutatePackageConfirmation();
  const [collapsed, setCollapsed] = useState(true);

  const { packageInformationData, packageSessionData } = useContext(BookingFlowSummaryContext);

  const mainBooker = packageInformationData?.participants[0];

  const onNextStep = useCallback(async (type: 'booking' | 'quotation') => {
    // This should never happen, but for types sake we need to check if the form data is set.
    if (!packageInformationData?.session_id) {
      throw new Error('Session ID is not set');
    }

    setIsLoading(true);

    try {
      // Mutate the package confirmation to the backend.
      await packageConfirmationMutation.mutateAsync({
        session_id: packageInformationData.session_id,
        type,
      });

      // If a thanks page URL is set, redirect to it.
      if (options.thanksPageUrl) {
        window.location.href = options.thanksPageUrl;
      } else {
        // If the mutation is successful, go to the next step.
        navigate(bookingFlowRoutes.THANKS);
      }
    } catch (error) {
      // Errors, validation errors are handled by the mutation object.
      // So we don't have to handle them here.
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageInformationData?.session_id]);

  const submitButton = useMemo(() => {
    return (
      <div className={summaryStyles.buttons}>
        <Button
          onClick={() => onNextStep('booking')}
          disabled={isLoading}
          loading={isLoading}
          loadingText={translate('bookingFlow.saving')}
        >
          {translate('bookingFlow.confirm_booking')}
          <IconArrowRight />
        </Button>

        <Button
          onClick={() => onNextStep('quotation')}
          disabled={isLoading}
          loading={isLoading}
          loadingText={translate('bookingFlow.saving')}
          styleType="link"
        >
          {translate('bookingFlow.request_quotation')}
          <IconArrowRight />
        </Button>
      </div>
    );
  }, [
    translate,
    onNextStep,
    isLoading,
  ]);

  const bikeRiderHeightForParticipant = useCallback((participantIndex: number) => {
    if (!packageInformationData?.bike_rider_height) {
      return null;
    }

    return packageInformationData.bike_rider_height
      .find((bikeRiderHeight) => bikeRiderHeight.participant_index === participantIndex);
  }, [packageInformationData?.bike_rider_height]);

  if (!packageInformationData?.session_id || !packageSessionData) {
    return null;
  }

  const { summary } = packageSessionData;

  return (
    <BookingFlowLayout
      step={2}
      sidebarVisible={false}
      showDisclaimer
      sidebarActions={submitButton}
      contentActions={submitButton}
      summary={summary}
    >
      <h1 className={utilStyles.srOnly}>
        Summary
      </h1>

      <Button
        type="router-link"
        href={bookingFlowRoutes.INFORMATION}
        disabled={isLoading}
        styleType="outlinePrimary"
        className={utilStyles.mbSmall}
      >
        <IconArrowLeft />
        <span>{translate('bookingFlow.back')}</span>
      </Button>

      <InformationCard
        title={`${translate('bookingFlow.summary.overview_title')}: ${summary.title}`}
        className={utilStyles.mbMedium}
      >
        <div className={summaryStyles.summaryOverview}>
          <div className={summaryStyles.summaryLines}>
            <div className={summaryStyles.summaryLinesTable}>
              <PriceStatistic
                lines={summary.lines}
                total={summary.total}
              />
            </div>
          </div>

          <div className={summaryStyles.summarySidebar}>
            <Summary
              summary={summary}
              className={summaryStyles.summarySidebarSummary}
            />

            {mainBooker && (
              <SummaryMainBooker
                mainBooker={packageInformationData?.main_booker}
                mainBookerParticipant={mainBooker}
              />
            )}

            {packageInformationData?.address && (
              <SummaryAddressInformation
                address={packageInformationData?.address}
              />
            )}

            {packageInformationData.promo_code && (
              <SummaryPromoCode
                promoCode={packageInformationData.promo_code}
              />
            )}

            {
              packageSessionData.room_types
              && packageSessionData.room_types.length > 0
              && packageSessionData.room_types_selected.length > 0
              && (
                <SummaryRoomTypes
                  roomTypes={packageSessionData.room_types}
                  roomTypesSelected={packageSessionData.room_types_selected}
                />
              )
            }
          </div>
        </div>
      </InformationCard>

      <InformationCard
        title={translate('bookingFlow.summary.participants_title')}
        className={utilStyles.mbMedium}
      >
        <div className={summaryStyles.summaryParticipants}>
          {packageInformationData?.participants.map((participant) => {
            const bikeRiderHeightForCurrentParticipant = bikeRiderHeightForParticipant(
              participant.index,
            );

            return (
              <div key={participant.index} className={summaryStyles.summaryParticipant}>
                <strong>
                  {translate('bookingFlow.summary.participant', { index: participant.index + 1 })}
                </strong>

                {participant.first_name && participant.last_name && (
                  <>
                    <br />
                    {`${translate('form.name')}: ${participant.first_name} ${participant.last_name}`}
                  </>
                )}

                {participant.birth_date && (
                  <>
                    <br />
                    {`${translate('form.birth_date')}: ${formatDate(participant.birth_date, 'd-m-Y')}`}
                  </>
                )}

                {bikeRiderHeightForCurrentParticipant?.height_cm && (
                  <>
                    <br />
                    <span data-testid="bike-rider-height">
                      {`${translate('bookingFlow.summary.bike_rider_height')}: ${bikeRiderHeightForCurrentParticipant?.height_cm}`}
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </InformationCard>

      {packageInformationData.debug && packageInformationData.debug.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <button type="button" onClick={() => setCollapsed(!collapsed)} className="underline">
            Debug
          </button>

          <pre style={{ fontFamily: 'monospace', fontSize: '12px' }} hidden={collapsed}>
            {JSON.stringify(packageInformationData.debug, null, 2)}
          </pre>
        </div>
      )}
    </BookingFlowLayout>
  );
}

export default BookingFlowSummary;
